<template>
  <details class="ai-author">
    <summary>AI 流程助手 · 描述需求，生成流程方案</summary>
    <p>针对当前咨询分类修改流程、添加步骤和分支，或重新设计完整流程。方案确认后写入当前草稿，发布并启用后才影响玩家。</p>
    <p class="scope">新增咨询分类请先点“新增咨询流程”，再让助手完善流程和识别规则。新增业务接口仍需开发接入。</p>
    <label>你希望如何调整？<textarea v-model="instruction" aria-label="流程修改需求" rows="4" maxlength="4000" :disabled="busy" placeholder="例如：增加一个确认订单号的步骤，玩家确认后再查询订单。" /></label>
    <button type="button" :disabled="busy || !instruction.trim() || blocked" @click="generate(false)">生成方案</button>
    <button v-if="jobId && proposal" type="button" :disabled="busy || !instruction.trim() || blocked" @click="generate(true)">{{ proposal.valid ? '根据补充要求继续调整' : '按校验错误重新生成' }}</button>
    <p v-if="blocked">请先保存或放弃当前节点、源码的修改。</p>
    <p v-if="status" role="status">{{ status }}</p><p v-if="error" role="alert">{{ error }}</p>
    <section v-if="proposal" class="proposal">
      <h3>生成方案 · 尚未保存</h3><p class="summary">{{ proposal.summary }}</p>
      <p v-if="!proposal.valid" role="alert">校验未通过：{{ proposal.error }}。此方案不能保存，可继续调整。</p>
      <template v-else>
        <p>结构和引用校验通过。请核对业务逻辑，并在保存后使用“离线模拟”测试。</p>
        <p v-if="JSON.stringify(proposal.before?.intentDefinition) !== JSON.stringify(proposal.after?.intentDefinition)">咨询分类的名称、说明或识别规则也已修改，请展开源码对比核对。</p>
        <ul><li v-for="change in changes" :key="change">{{ change }}</li></ul>
        <WorkflowGraph v-if="proposal.graph" :graph="proposal.graph" :selected="selected?.id" @select="selected = $event" />
        <WorkflowFields v-if="selected" :value="selected.definition" />
      </template>
      <details><summary>查看修改前后与生成的 YAML</summary><div class="comparison"><div><h4>修改前</h4><pre>{{ JSON.stringify(proposal.before || resource.document, null, 2) }}</pre></div><div><h4>修改后</h4><pre>{{ proposal.content }}</pre></div></div></details>
      <button type="button" class="primary" :disabled="busy || blocked || !proposal.valid" @click="apply">确认写入当前草稿</button>
      <button type="button" :disabled="busy" @click="clear">放弃方案</button>
    </section>
  </details>
</template>
<script setup>
/* global defineProps, defineEmits, defineExpose */
import { ref, computed, onBeforeUnmount } from 'vue'
import { ElMessageBox } from 'element-plus'
import { workflowRequest } from '@/api/ai-workflows'
import WorkflowGraph from './WorkflowGraph.vue'
import WorkflowFields from './WorkflowFields.vue'
const props = defineProps({ site: Object, draft: Object, resource: Object, blocked: Boolean })
const emit = defineEmits(['applied'])
const instruction = ref(''), busy = ref(false), error = ref(''), status = ref(''), jobId = ref(''), proposal = ref(null), selected = ref(null)
let disposed = false, timer, resume
onBeforeUnmount(() => { disposed = true; clearTimeout(timer); resume?.() })
const request = (method, suffix = '', body) => workflowRequest(props.site.id, method, suffix, body, undefined, 'ai-workflow-author')
const changes = computed(() => {
 const before = proposal.value?.before?.states || {}, after = proposal.value?.after?.states || {}
 return [...new Set([...Object.keys(before), ...Object.keys(after)])].flatMap(id => {
  if (!before[id]) return [`新增步骤：${after[id].title || id}`]
  if (!after[id]) return [`删除步骤：${before[id].title || id}`]
  return JSON.stringify(before[id]) !== JSON.stringify(after[id]) ? [`修改步骤：${after[id].title || id}`] : []
 })
})
async function run(fn) { if (busy.value || props.blocked) return; busy.value = true; error.value = ''; try { await fn() } catch (e) { if (!disposed) { error.value = e.response?.data?.message || e.message; status.value = '' } } finally { busy.value = false } }
async function generate(continuePrevious) { await run(async () => {
 status.value = '正在提交生成请求…'
 const next = await request('POST', '', { draftId: props.draft.id, expectedRevision: props.draft.revision, key: props.resource.resource_key, instruction: instruction.value, previousJobId: continuePrevious ? jobId.value : null })
 if (!next?.jobId) throw new Error('未返回生成任务编号')
 jobId.value = next.jobId; proposal.value = null; selected.value = null
 status.value = '正在生成并校验流程，请稍候…'
 const deadline = Date.now() + 145000
 while (!disposed && Date.now() < deadline) {
  const result = await request('GET', `/${jobId.value}`)
  if (disposed) return
  if (result.status === 'DONE') { proposal.value = result; status.value = result.valid ? '方案已生成，请预览后确认。' : '方案未通过校验。'; return }
  if (result.status !== 'PENDING') throw new Error('生成超时，请稍后重试')
  await new Promise(resolve => { resume = resolve; timer = setTimeout(resolve, 1500) })
 }
 if (!disposed) throw new Error('生成超时，请稍后重试')
}) }
async function apply() {
 if (busy.value || props.blocked || !proposal.value?.valid) return
 try { await ElMessageBox.confirm('将此方案写入当前流程草稿？当前线上版本不会改变。', '确认保存流程方案') } catch { return }
 await run(async () => { const draft = await request('POST', `/${jobId.value}/apply`); clear(); status.value = '已写入草稿，请进行离线模拟并按现有流程发布。'; emit('applied', draft) })
}
function clear() { proposal.value = null; selected.value = null; jobId.value = ''; status.value = ''; error.value = '' }
async function discard(commit = true) {
 if (busy.value) return false
 if (!proposal.value && !instruction.value.trim()) return true
 try { await ElMessageBox.confirm('离开后将清除当前需求和未保存的 AI 方案，是否继续？', '离开流程助手'); if (commit) reset(); return true } catch { return false }
}
function reset() { clear(); instruction.value = '' }
defineExpose({ busy, discard, reset })
</script>
<style scoped>
.ai-author { margin:20px 0; padding:18px; border:1px solid #bfdbfe; border-radius:12px; background:#f8fbff; } summary { cursor:pointer; font-weight:600; } p { line-height:1.8; }.scope { color:#64748b; font-size:13px; } textarea { display:block; width:100%; box-sizing:border-box; padding:12px; margin:10px 0; border:1px solid #cbd5e1; border-radius:8px; font:inherit; }button { padding:10px 14px; margin:8px 8px 8px 0; border:1px solid #cbd5e1; border-radius:8px; background:white; cursor:pointer; }button:disabled { opacity:.5; cursor:not-allowed; }.primary { background:#2563eb; color:white; }.summary { white-space:pre-wrap; }[role=alert] { color:#b91c1c; }.comparison { display:grid; grid-template-columns:1fr 1fr; gap:16px; }.comparison > div { min-width:0; }pre { max-height:400px; overflow:auto; white-space:pre-wrap; overflow-wrap:anywhere; font-size:12px; }.proposal { border-top:1px solid #dbeafe; margin-top:16px; }@media(max-width:800px){.comparison{grid-template-columns:1fr}}
</style>
